import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ShoppingBag, X, Plus, Minus, ArrowRight, Mail, FileText, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS = []; // Catalog is 100% from Apliiq product store

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/b027f13e-8be5-40c1-b971-21eeca81b155/images/a89038466850456e134cb190c1213020e0e8a7d5652371511113c2dba740e640.png';

/* ─── Fetch products from API (core + Apliiq) ────────────── */
const useProducts = () => {
  const [products, setProducts] = useState(PRODUCTS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/heir/products`);
        if (res.data && res.data.length > 0) {
          setProducts(res.data);
        }
      } catch {
        // fallback to hardcoded
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return { products, loading };
};

/* ─── Cart helpers ────────────────────────────────────────── */
const useCart = () => {
  const [items, setItems] = useState([]);
  const add = useCallback((product, size) => {
    setItems(prev => {
      const key = `${product.id}_${size}`;
      const idx = prev.findIndex(i => i.key === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { key, product, size, qty: 1 }];
    });
  }, []);
  const remove = useCallback((key) => setItems(prev => prev.filter(i => i.key !== key)), []);
  const updateQty = useCallback((key, delta) => {
    setItems(prev => prev.map(i => {
      if (i.key !== key) return i;
      const newQty = i.qty + delta;
      return newQty > 0 ? { ...i, qty: newQty } : i;
    }).filter(i => i.qty > 0));
  }, []);
  const total = items.reduce((s, i) => s + i.product.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  return { items, add, remove, updateQty, total, count };
};

/* ─── Description Renderer (handles Apliiq HTML descriptions) ─── */
const stripHtml = (s) => {
  if (!s || typeof s !== 'string') return '';
  return s.replace(/<\/(?:h[1-6]|p|div|li|br)\s*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').replace(/&#39;/g, "'").replace(/&quot;/g, '"');
};

const ProductDescription = ({ product }) => {
  // If backend already sent structuredDescription, use it
  if (product.structuredDescription && product.structuredDescription.length > 0) {
    const items = product.structuredDescription;
    const elements = [];
    let bulletBuffer = [];

    const flushBullets = () => {
      if (bulletBuffer.length > 0) {
        elements.push(
          <ul key={`bl-${elements.length}`} className="space-y-1 mb-2">
            {bulletBuffer.map((b, i) => (
              <li key={i} className="text-xs text-gray-500 leading-relaxed flex items-start gap-2">
                <span className="text-[#C0C0C0]/40 mt-1 text-[6px]">●</span>{b}
              </li>
            ))}
          </ul>
        );
        bulletBuffer = [];
      }
    };

    for (const item of items) {
      if (item.type === 'bullet') {
        bulletBuffer.push(item.text);
      } else {
        flushBullets();
        if (item.type === 'label') {
          elements.push(<p key={`l-${elements.length}`} className="text-[8px] uppercase tracking-[0.2em] text-[#C0C0C0]/40 mt-3 mb-1">{item.text}</p>);
        } else {
          elements.push(<p key={`p-${elements.length}`} className="text-xs text-gray-500 leading-relaxed mb-1">{item.text}</p>);
        }
      }
    }
    flushBullets();
    return <div className="mb-5 max-h-40 overflow-y-auto">{elements}</div>;
  }

  // Fallback: strip HTML from raw description
  const clean = stripHtml(product.description || '').trim();
  if (!clean) return null;
  return <p className="text-xs text-gray-500 leading-relaxed mb-5">{clean}</p>;
};

/* ─── Product Card ────────────────────────────────────────── */
const ProductCard = ({ product, onAddToCart }) => {
  const [imgIdx, setImgIdx] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState(product.color || '');

  const colorOptions = product.colorOptions || null;
  const activeOption = colorOptions?.find(o => o.color === selectedColor) || null;
  const displayColor = selectedColor || product.color;
  const displayImages = activeOption?.images || product.images || [];
  const displayLabels = activeOption?.imageLabels || product.imageLabels || [];
  const displaySizes = activeOption?.sizes || product.sizes || [];
  const displayPrice = activeOption?.price || product.price;

  const handleColorChange = (color) => {
    setSelectedColor(color);
    setSelectedSize('');
    setImgIdx(0);
  };

  const handleAdd = () => {
    if (!selectedSize) { toast.error('Please select a size'); return; }
    const cartProduct = { ...product, color: displayColor, price: displayPrice };
    onAddToCart(cartProduct, selectedSize);
    toast.success(`${product.name} — ${displayColor} (${selectedSize}) added to cart`);
  };

  return (
    <div className="group bg-[#111111] border border-white/5 hover:border-[#C0C0C0]/30 transition-all duration-500" data-testid={`product-card-${product.id}`}>
      {/* Image area */}
      <div className="relative aspect-square overflow-hidden bg-[#0a0a0a] cursor-pointer" onClick={() => setImgIdx(i => (i + 1) % displayImages.length)}>
        <img
          src={displayImages[imgIdx] || displayImages[0]}
          alt={`${product.name} — ${displayLabels[imgIdx] || ''}`}
          className={`w-full h-full object-cover transition-all duration-500 ${product.invert ? 'invert' : ''}`}
        />
        {displayImages.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {displayImages.map((_, i) => (
              <button key={i} onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                className={`w-1.5 h-1.5 rounded-full transition-all ${i === imgIdx ? 'bg-[#C0C0C0] scale-125' : 'bg-white/30'}`}
                aria-label={displayLabels[i]} />
            ))}
          </div>
        )}
        {displayImages.length > 1 && (
          <span className="absolute top-3 right-3 text-[8px] uppercase tracking-widest text-[#C0C0C0]/60 bg-black/60 px-2 py-0.5">
            {displayLabels[imgIdx]}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="p-5 md:p-6">
        <p className="text-[9px] uppercase tracking-[0.3em] text-[#C0C0C0]/50 mb-2">{displayColor}</p>
        <h3 className="text-lg font-serif italic text-[#E8E8E8] mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {product.name}
        </h3>
        <ProductDescription product={product} />

        {/* Color selector (only when colorOptions exist) */}
        {colorOptions && colorOptions.length > 1 && (
          <div className="mb-4">
            <p className="text-[9px] uppercase tracking-[0.2em] text-gray-600 mb-2">Color</p>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map(opt => (
                <button key={opt.color} onClick={() => handleColorChange(opt.color)}
                  data-testid={`color-${product.id}-${opt.color}`}
                  className={`px-3.5 py-2.5 text-[10px] uppercase tracking-wider border transition-all duration-300 min-h-[44px] ${
                    selectedColor === opt.color
                      ? 'border-[#C0C0C0] text-white bg-[#C0C0C0]/10'
                      : 'border-white/10 text-gray-500 hover:border-white/25 hover:text-gray-300'
                  }`}>
                  {opt.color}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Size selector */}
        <div className="mb-5">
          <p className="text-[9px] uppercase tracking-[0.2em] text-gray-600 mb-2">Size</p>
          <div className="flex flex-wrap gap-2">
            {displaySizes.map(s => (
              <button key={s} onClick={() => setSelectedSize(s)}
                data-testid={`size-${product.id}-${s}`}
                className={`px-3.5 py-2.5 text-[10px] uppercase tracking-wider border transition-all duration-300 min-h-[44px] ${
                  selectedSize === s
                    ? 'border-[#C0C0C0] text-white bg-[#C0C0C0]/10'
                    : 'border-white/10 text-gray-500 hover:border-white/25 hover:text-gray-300'
                }`}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Price + Add */}
        <div className="flex items-center justify-between pt-4 border-t border-white/5">
          <span className="text-[#E8E8E8] font-serif text-2xl" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            ${displayPrice}
          </span>
          <button onClick={handleAdd} data-testid={`add-to-cart-${product.id}`}
            className="px-5 py-2.5 text-[9px] uppercase tracking-[0.2em] font-medium border border-[#C0C0C0]/30 text-[#E8E8E8] bg-transparent hover:bg-[#C0C0C0]/10 hover:border-[#C0C0C0]/60 transition-all duration-400 flex items-center gap-2">
            <ShoppingBag className="w-3.5 h-3.5" />
            Add to Cart
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── Cart Drawer ─────────────────────────────────────────── */
const CartDrawer = ({ open, onClose, cart, onCheckout, loading }) => {
  const drawerRef = useRef(null);

  useEffect(() => {
    if (open) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 backdrop-blur-sm" onClick={onClose} />
      <div ref={drawerRef} className="fixed top-0 right-0 h-full w-full max-w-md bg-[#111111] border-l border-white/5 z-50 flex flex-col" data-testid="cart-drawer">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/5">
          <h2 className="text-lg font-serif italic text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Your Cart ({cart.count})
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors" data-testid="close-cart">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {cart.items.length === 0 && (
            <p className="text-gray-600 text-center py-16 text-sm">Your cart is empty</p>
          )}
          {cart.items.map(item => (
            <div key={item.key} className="flex gap-4 pb-5 border-b border-white/5" data-testid={`cart-item-${item.key}`}>
              <img src={item.product.images[0]} alt={item.product.name}
                className={`w-20 h-20 object-cover bg-[#0a0a0a] ${item.product.invert ? 'invert' : ''}`} />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#E8E8E8] font-medium truncate">{item.product.name}</p>
                <p className="text-[10px] text-gray-600 uppercase tracking-wider mt-0.5">Size: {item.size}</p>
                <div className="flex items-center justify-between mt-3">
                  <div className="flex items-center border border-white/10">
                    <button onClick={() => cart.updateQty(item.key, -1)} className="px-2 py-1 text-gray-500 hover:text-white transition-colors">
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="px-3 text-sm text-white">{item.qty}</span>
                    <button onClick={() => cart.updateQty(item.key, 1)} className="px-2 py-1 text-gray-500 hover:text-white transition-colors">
                      <Plus className="w-3 h-3" />
                    </button>
                  </div>
                  <span className="text-[#E8E8E8] text-sm font-medium">${item.product.price * item.qty}</span>
                </div>
              </div>
              <button onClick={() => cart.remove(item.key)} className="text-gray-600 hover:text-red-400 transition-colors self-start">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        {/* Footer */}
        {cart.items.length > 0 && (
          <div className="p-6 border-t border-white/5 bg-[#0a0a0a]">
            <div className="flex items-center justify-between mb-5">
              <span className="text-sm text-gray-400 uppercase tracking-wider">Subtotal</span>
              <span className="text-2xl font-serif text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                ${cart.total}
              </span>
            </div>
            <button onClick={onCheckout} disabled={loading} data-testid="checkout-btn"
              className="w-full py-4 text-[10px] uppercase tracking-[0.3em] font-medium bg-[#C0C0C0] text-[#0a0a0a] hover:bg-[#E8E8E8] disabled:opacity-50 transition-all duration-300">
              {loading ? 'Redirecting to Stripe...' : 'Checkout'}
            </button>
            <p className="text-[9px] text-gray-600 text-center mt-3">Shipping & tax calculated at checkout</p>
          </div>
        )}
      </div>
    </>
  );
};

/* ─── Main Page ───────────────────────────────────────────── */
const HeirPage = () => {
  const cart = useCart();
  const { products } = useProducts();
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      toast.success('Order placed! Thank you for shopping HEIR®.');
    }
  }, [searchParams]);

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    try {
      const items = cart.items.map(i => ({
        lookup_key: i.product.lookup_key,
        quantity: i.qty,
        size: i.size,
      }));
      const res = await axios.post(`${API}/api/heir/checkout`, {
        items,
        origin_url: window.location.origin,
      });
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(res.data.error || 'Checkout failed');
      }
    } catch (err) {
      toast.error('Checkout failed. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="heir-page">

      {/* ── Floating Cart Button ── */}
      {cart.count > 0 && (
        <button onClick={() => setCartOpen(true)} data-testid="cart-fab"
          className="fixed bottom-6 right-6 z-[9999999999] w-14 h-14 bg-[#C0C0C0] text-[#0a0a0a] rounded-full flex items-center justify-center shadow-2xl hover:bg-[#E8E8E8] transition-all duration-300">
          <ShoppingBag className="w-5 h-5" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-white text-black text-[10px] font-bold rounded-full flex items-center justify-center">
            {cart.count}
          </span>
        </button>
      )}

      {/* ── Cart Drawer ── */}
      <CartDrawer open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} onCheckout={handleCheckout} loading={checkoutLoading} />

      {/* ══════════════ 1. HERO ══════════════ */}
      <section className="relative min-h-[100vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-[#0a0a0a]" />
        {/* Hero image — right side */}
        <div className="absolute right-0 top-0 w-1/2 h-full hidden md:block">
          <img src={HERO_IMG} alt="HEIR® lifestyle" className="w-full h-full object-cover object-center opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
        </div>
        {/* Mobile hero bg */}
        <div className="absolute inset-0 md:hidden">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-[#0a0a0a]/80" />
        </div>

        <div className="relative z-10 px-6 md:px-16 lg:px-24 max-w-3xl pt-24 pb-16">
          <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#C0C0C0]/20 to-transparent mb-10" />
          <h1 data-testid="heir-logo">
            <span className="text-7xl md:text-[8rem] lg:text-[10rem] font-serif italic text-[#C0C0C0] tracking-wider leading-none block" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              HEIR
            </span>
            <sup className="text-[#C0C0C0] text-xl md:text-2xl relative -top-10 md:-top-16 ml-1">&reg;</sup>
          </h1>
          <p className="text-[10px] uppercase tracking-[0.5em] text-gray-600 mt-2 mb-10">by Meet Mr. Fashion</p>
          <p className="text-2xl md:text-3xl font-serif italic text-[#E8E8E8] mb-10" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Own the Legacy
          </p>
          <a href="#collection">
            <button className="px-10 py-4 text-[10px] uppercase tracking-[0.3em] font-medium border border-[#C0C0C0]/30 text-[#E8E8E8] bg-transparent hover:bg-[#C0C0C0]/10 hover:border-[#C0C0C0]/60 transition-all duration-500 flex items-center gap-3" data-testid="shop-now-hero">
              Shop Now
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </a>
        </div>
      </section>

      {/* ══════════════ 2. PRODUCTS ══════════════ */}
      <section id="collection" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C0C0C0]/50 mb-5">The Collection</p>
            <h2 className="text-3xl md:text-4xl font-serif italic text-[#E8E8E8]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              HEIR&reg; Essentials
            </h2>
          </div>
          {products.length > 0 ? (
            <div className={`grid grid-cols-1 ${products.length === 1 ? 'max-w-md mx-auto' : products.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' : 'md:grid-cols-2 lg:grid-cols-3'} gap-6 md:gap-8`}>
              {products.map(p => (
                <ProductCard key={p.id} product={p} onAddToCart={cart.add} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-2xl font-serif italic text-[#E8E8E8] mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                New drop coming soon
              </p>
              <p className="text-sm text-gray-500 mb-8 max-w-md mx-auto">
                The next collection is being crafted. Leave your email and we'll notify you the moment it lands.
              </p>
              <form onSubmit={(e) => { e.preventDefault(); const email = e.target.elements.email.value; if (email) { axios.post(`${API}/api/subscribe`, { email }).then(() => toast.success('You\'re on the list.')).catch(() => toast.error('Try again.')); e.target.reset(); }}}
                className="flex gap-3 max-w-sm mx-auto">
                <input name="email" type="email" required placeholder="your@email.com"
                  className="flex-1 bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder-gray-600 focus:border-[#C0C0C0]/40 focus:outline-none transition-colors" />
                <button type="submit"
                  className="px-6 py-3 text-[9px] uppercase tracking-[0.2em] font-medium bg-[#C0C0C0] text-[#0a0a0a] hover:bg-[#E8E8E8] transition-all duration-300">
                  Notify Me
                </button>
              </form>
            </div>
          )}
        </div>
      </section>

      {/* ══════════════ 3. CORPORATE GIFTING ══════════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-[#111111] border-t border-white/5">
        {/* Chrome accent rule */}
        <div className="w-16 h-px bg-gradient-to-r from-transparent via-[#C0C0C0]/40 to-transparent mx-auto mb-16" />
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#C0C0C0]/50 mb-5">Corporate Program</p>
          <h2 className="text-3xl md:text-4xl font-serif italic text-[#E8E8E8] mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            Outfit Your Team
          </h2>
          <p className="text-gray-500 leading-relaxed mb-10 max-w-xl mx-auto">
            Bulk orders starting at 25 units. Custom co-branding available at 250+. Boardroom-ready packaging included.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/heir/deck" data-testid="view-deck-btn">
              <button className="px-8 py-4 text-[10px] uppercase tracking-[0.25em] font-medium border border-[#C0C0C0]/30 text-[#E8E8E8] bg-transparent hover:bg-[#C0C0C0]/10 hover:border-[#C0C0C0]/60 transition-all duration-500 flex items-center gap-2 justify-center w-full sm:w-auto">
                <FileText className="w-4 h-4" />
                View Gifting Deck
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </Link>
            <a href="mailto:Akeem@meetmr.fashion?subject=HEIR%C2%AE%20Corporate%20Gifting%20Inquiry" data-testid="corporate-quote-btn">
              <button className="px-8 py-4 text-[10px] uppercase tracking-[0.25em] font-medium bg-[#C0C0C0] text-[#0a0a0a] hover:bg-[#E8E8E8] transition-all duration-300 flex items-center gap-2 justify-center w-full sm:w-auto">
                <Mail className="w-4 h-4" />
                Request a Quote
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════ 4. FOOTER ══════════════ */}
      <footer className="py-16 px-6 md:px-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <p className="font-serif italic text-[#C0C0C0] text-2xl mb-1" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                HEIR<sup className="text-sm">&reg;</sup>
              </p>
              <p className="text-[9px] uppercase tracking-[0.4em] text-gray-600">by Meet Mr. Fashion</p>
            </div>
            <nav className="flex gap-8">
              <a href="#collection" className="text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-[#C0C0C0] transition-colors">Shop</a>
              <a href="mailto:Akeem@meetmr.fashion" className="text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-[#C0C0C0] transition-colors">Contact</a>
              <Link to="/heir/deck" className="text-[10px] uppercase tracking-[0.2em] text-gray-500 hover:text-[#C0C0C0] transition-colors">Corporate Gifting</Link>
            </nav>
          </div>
          <div className="w-full h-px bg-white/5 my-8" />
          <p className="text-[9px] text-gray-700 text-center tracking-wider">&copy; 2026 Meet Mr. Fashion, Inc. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default HeirPage;
